import type { ChipColor } from '@/types/chip';
import type { TutoringAgreementRecord } from '@/types/entities';
import { Box, Typography, Paper, Grid, Chip, IconButton, Divider, Button, Table, TableBody, TableCell, TableHead, TableRow, Alert, Snackbar } from '@mui/material';
import { Description, CalendarMonth, Close, Person, Business, Link as LinkIcon, Schedule } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useState, useCallback } from 'react';

interface ScheduleItem {
  id: string;
  status: string;
  tutorName?: string;
  clientName?: string;
  subject?: string;
  agreementNumber?: string;
}

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Expired: 'error',
  Terminated: 'warning',
};

export const TutoringAgreementDetail = () => {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/tutoring-agreements')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Tutoring Agreement"
            description="A formal agreement between a tutor and client defining the tutoring relationship, services, and terms."
            icon={<Description />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #4527a0, #512da8)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Description sx={{ fontSize: 48 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.id}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.tutorName || record.tutor?.id || '—'} · {record.clientName || record.client?.id || '—'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Description color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Agreement Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Agreement #</Typography>
                    <Typography variant="body1" fontWeight={500}>{record.id}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Tutor</Typography>
                    {record.tutor?.id ? (
                      <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => navigate(`/tutors/${record.tutor.id}/show`)}>
                        {record.tutorName || record.tutor.id}
                      </Button>
                    ) : (
                      <Typography variant="body1">—</Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Client</Typography>
                    {record.client?.id ? (
                      <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => navigate(`/clients/${record.client.id}/show`)}>
                        {record.clientName || record.client.id}
                      </Button>
                    ) : (
                      <Typography variant="body1">—</Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Start Date</Typography>
                        <Typography variant="body1">{record.effectiveFrom || '—'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">End Date</Typography>
                        <Typography variant="body1">{record.effectiveTo || '—'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Terms</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Services</Typography>
                    <Typography variant="body1">{record.services?.length || 0} service(s)</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Locations</Typography>
                    <Typography variant="body1">{record.locations?.length || 0} location(s)</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AgreementLinkedSchedules agreementId={record.id} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AgreementTransitionActions record={record} snackbar={snackbar} setSnackbar={setSnackbar} />
            </Grid>
          </Grid>

          <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      );
    }} />
  );
};

const AgreementLinkedSchedules = ({ agreementId }: { agreementId: string }) => {
  const { data: schedules } = useGetList('tutoring-schedules', { filter: { agreementId } });
  const records = (schedules || []).slice(0, 10);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Schedule color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Linked Schedules</Typography>
        <Chip label={`${records.length} records`} size="small" sx={{ ml: 1 }} />
      </Box>
      {records.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Schedule</TableCell>
              <TableCell>Tutor</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((s: ScheduleItem) => (
              <TableRow key={s.id}>
                <TableCell><Chip label={s.id} size="small" /></TableCell>
                <TableCell>{s.tutorName || '—'}</TableCell>
                <TableCell>{s.subject || '—'}</TableCell>
                <TableCell><Chip label={s.status} size="small" color={s.status === 'Active' ? 'success' : 'default'} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No linked schedules</Typography>
      )}
    </Paper>
  );
};

const AgreementTransitionActions = ({ record, snackbar, setSnackbar }: {
  record: TutoringAgreementRecord;
  snackbar: { open: boolean; message: string; severity: 'success' | 'error' };
  setSnackbar: React.Dispatch<React.SetStateAction<{ open: boolean; message: string; severity: 'success' | 'error' }>>;
}) => {
  const transitions: { event: string; label: string; color: 'success' | 'warning' | 'error'; from: string[] }[] = [
    { event: 'approve', label: 'Approve', color: 'success', from: ['Draft', 'Pending Approval'] },
    { event: 'suspend', label: 'Suspend', color: 'warning', from: ['Active'] },
    { event: 'terminate', label: 'Terminate', color: 'error', from: ['Active', 'Suspended'] },
  ];

  const available = transitions.filter(t => t.from.includes(record.status));

  if (available.length === 0) return null;

  const handleTransition = async (event: string) => {
    try {
      const res = await fetch(`/api/v1/workflow-instances/${record.id}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event }),
      });
      if (!res.ok) throw new Error('Transition failed');
      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
      window.location.reload();
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Transition failed', severity: 'error' });
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Actions</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {available.map(t => (
          <Button key={t.event} variant="outlined" color={t.color} onClick={() => handleTransition(t.event)}>
            {t.label}
          </Button>
        ))}
      </Box>
    </Paper>
  );
};
